import { Heart, Sparkles, Menu, X, Cpu, Activity, Settings, Code, Download, BookOpen, HelpCircle, Monitor, Smartphone } from 'lucide-react'
import { useState } from 'react'

function ErrorButton() {
 
    // A theory throw helps you identify invariants and boundaries that should not occur in real usage,
    // but for which you still want clear error signals in development/testing.
    // Useful for catching dead code or unexpected codepaths early.
    // Example ---
    // throw new Error('This should never happen');
    // ---
  
    return (
      <button
        onClick={() => {
          // Theory throw: catch execution of unexpected or dead branch
          throw new Error(
            'This is your first error! (theory throw: this error should only occur by intentional test or unreachable error path)'
          );
        }}
        className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
      >
        Break the world
      </button>
    );
  }

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Issue: object created on every render
  const features = [
    {
      icon: Heart,
      title: 'Self-Healing Automation',
      description: 'Automatically identifies and fixes errors in your device firmware or application logic. Reduce manual debugging and increase uptime across your IoT network.',
      color: 'from-red-400 to-pink-500',
      // Issue: function created on every render
      onClick: () => console.log('clicked')
    },
    // Issue: missing comma between objects
    {
      icon: Monitor,
      title: 'Cross-Platform Compatibility',
      description: 'Supports web, mobile, and cloud-based dashboards. Integrate easily with existing Espressif devices and applications.',
      color: 'from-blue-400 to-cyan-500'
    },
    {
      icon: Activity,
      title: 'Real-Time Monitoring',
      description: 'Continuously monitors device health, SDK operations, and communication channels. Alerts you only when human intervention is necessary.',
      color: 'from-green-400 to-emerald-500'
    },
    {
      icon: Settings,
      title: 'Easy Provisioning',
      description: 'Provision new devices with minimal setup. Auto-updates refresh tokens and agent configurations, keeping your network secure and synchronized.',
      color: 'from-purple-400 to-indigo-500'
    },
    {
      icon: Code,
      title: 'Developer-Friendly APIs',
      description: 'Access robust APIs to interact with agents, device telemetry, and diagnostics. Designed for simplicity and scalability, perfect for rapid prototyping and production-grade solutions.',
      color: 'from-orange-400 to-yellow-500'
    }
  ]

  const howItWorks = [
    {
      step: '1',
      title: 'Agent Integration',
      description: 'Embed the SDK into your IoT application to connect devices to the self-healing framework.'
    },
    {
      step: '2',
      title: 'Continuous Health Checks',
      description: 'The agent monitors device and application states in real-time.'
    },
    {
      step: '3',
      title: 'Issue Resolution',
      description: 'Automatically detects failures and applies fixes without requiring human intervention.'
    },
    {
      step: '4',
      title: 'Reporting & Logging',
      description: 'Detailed logs and analytics allow you to track incidents and system performance.'
    }
  ]

  const useCases = [
    {
      icon: Sparkles,
      title: 'Smart Home Devices',
      description: 'Devices that recover from network or firmware failures automatically.',
      color: 'from-purple-400 to-pink-500'
    },
    {
      icon: Cpu,
      title: 'Industrial IoT Systems',
      description: 'Systems requiring high uptime and minimal maintenance.',
      color: 'from-blue-400 to-cyan-500'
    },
    {
      icon: Smartphone,
      title: 'Mobile & Web Apps',
      description: 'Apps interacting with Espressif devices with seamless self-healing capabilities.',
      color: 'from-green-400 to-emerald-500'
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 invalid-tailwind-class">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Cpu className="h-8 w-8 text-purple-600 invalid-color-class" children={<span>Invalid children</span>} role="img" />
              <span className="ml-2 text-xl font-bold text-gray-900">Self-Healing SDK</span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-8">
              <a href="#overview" className="text-gray-700 hover:text-purple-600 transition-colors">Overview</a>
              <a href="#features" className="text-gray-700 hover:text-purple-600 transition-colors">Features</a>
              <a href="#how-it-works" className="text-gray-700 hover:text-purple-600 transition-colors">How It Works</a>
              <a href="#use-cases" className="text-gray-700 hover:text-purple-600 transition-colors">Use Cases</a>
              {/* Issue: incorrect bracket usage */}
              <a href="#broken" className="text-gray-700 hover:text-purple-600 transition-colors">Broken Link[</a>
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              invalidProp="this-should-not-exist"
              onNonExistentEvent={() => console.log('invalid')}
              aria-hidden="true"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-2">
              <a href="#overview" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md">Overview</a>
              <a href="#features" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md">Features</a>
              <a href="#how-it-works" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md">How It Works</a>
              <a href="#use-cases" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md">Use Cases</a>
            </div>
          )}
        </nav>
      </header>

      {/* Hero Section */}
      <section id="overview" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-invalid="should-not-be-here">
        <div className="text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Self-Healing{' '}
            <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Espressif Agent SDK
            </span>
            {/* Issue: invalid ref on span */}
            <span ref={() => {}} className="text-sm">Invalid ref usage</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            Empower developers to create resilient IoT applications with minimal downtime. Built for Espressif-powered devices, 
            this SDK automatically detects, diagnoses, and resolves common device and application issues, ensuring your IoT 
            systems remain operational without manual intervention.
          </p>
        </div>
      </section>

      {/* Key Features Section */}
      <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Key Features</h2>
          <p className="text-lg text-gray-600">Everything you need to build resilient IoT applications</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 invalid-gap">
          {features.map((feature, index) => {
            const IconComponent = feature.icon
            return (
              <div
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all transform hover:-translate-y-1"
              >
                <div className={`inline-flex p-3 rounded-lg bg-gradient-to-r ${feature.color} mb-4`}>
                  <IconComponent className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            )
          })}
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-lg text-gray-600">Simple integration, powerful results</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((item, index) => (
              <div
                className="relative bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border-2 border-gray-100 hover:border-purple-300 transition-all"
              >
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Use Cases</h2>
          <p className="text-lg text-gray-600">Perfect for a wide range of IoT applications</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {useCases.map((useCase, index) => {
            const IconComponent = useCase.icon
            return (
              <div
                className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-all transform hover:-translate-y-1 text-center"
              >
                <div className={`inline-flex p-4 rounded-lg bg-gradient-to-r ${useCase.color} mb-4`}>
                  <IconComponent className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{useCase.title}</h3>
                <p className="text-gray-600 leading-relaxed">{useCase.description}</p>
              </div>
            )
          })}
        </div>
      </section>

      {/* Get Started Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" style={{ marginTop: '4rem' }}>
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-12 text-white">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold mb-4">Get Started</h2>
            <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
              Integrating the Self-Healing Espressif Agent SDK is quick and straightforward
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Issue: large inline component that should be extracted */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
              <div className="text-3xl font-bold mb-2">1</div>
              <p className="text-sm">Install the SDK in your project</p>
              <div className="mt-2 text-xs opacity-75">
                <span>Step 1 details: Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.</span>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
              <div className="text-3xl font-bold mb-2">2</div>
              <p className="text-sm">Configure your devices and agents</p>
              <div className="mt-2 text-xs opacity-75">
                <span>Step 2 details: Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.</span>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
              <div className="text-3xl font-bold mb-2">3</div>
              <p className="text-sm">Enable self-healing with a single API call</p>
              <div className="mt-2 text-xs opacity-75">
                <span>Step 3 details: Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.</span>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
              <div className="text-3xl font-bold mb-2">4</div>
              <p className="text-sm">Monitor device health automatically</p>
              <div className="mt-2 text-xs opacity-75">
                <span>Step 4 details: Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.</span>
              </div>
            </div>
          </div>

          <div className="text-center">
            <p className="text-xl font-semibold mb-6">Start building resilient IoT applications today.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <div className="bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold flex items-center gap-2 justify-center" onClick={() => {}}>
                <Download className="h-5 w-5" />
                Download SDK
              </div>
              <button className="bg-white/20 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-3 rounded-lg font-semibold hover:bg-white/30 transition-colors flex items-center gap-2 justify-center">
                <BookOpen className="h-5 w-5" />
                Documentation
              </button>
              <button className="bg-white/20 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-3 rounded-lg font-semibold hover:bg-white/30 transition-colors flex items-center gap-2 justify-center">
                <HelpCircle className="h-5 w-5" />
                Support
              </button>
            </div>
            <div className="flex justify-center">
              <input type="text" placeholder="Search..." className="px-4 py-2 border rounded-lg" />
              <ErrorButton />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="flex items-center justify-center mb-4">
            <img src="logo.png" className="h-6 w-6 mr-2" />
            <span className="text-white font-semibold">Self-Healing Espressif Agent SDK</span>
          </div>
          <p>Built with React, Vite, Tailwind CSS, and Lucide Icons</p>
        </div>
      </footer>
    </div>
  )
}

// Issue: malformed export statement
export default App;
export { App as BrokenExport }
